import { create } from "zustand";

export interface Settings {
  textSpeed: number;
  autoDelay: number;
  masterVolume: number;
  bgmVolume: number;
  sfxVolume: number;
  voiceVolume: number;
  failedChoiceDisplay: "hide" | "disabled";
  theme: "default" | "glass" | "neon" | "parchment";
}

const STORAGE_KEY = "browser_vn_settings";

const DEFAULTS: Settings = {
  textSpeed: 40,
  autoDelay: 3,
  masterVolume: 1,
  bgmVolume: 1,
  sfxVolume: 1,
  voiceVolume: 1,
  failedChoiceDisplay: "hide",
  theme: "default",
};

function loadFromStorage(): Settings {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { ...DEFAULTS };
    const parsed = JSON.parse(raw) as Partial<Settings>;
    return { ...DEFAULTS, ...parsed };
  } catch {
    return { ...DEFAULTS };
  }
}

function saveToStorage(settings: Settings): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
  } catch {
    // localStorage may be unavailable
  }
}

interface SettingsState extends Settings {
  set: <K extends keyof Settings>(key: K, value: Settings[K]) => void;
  reset: () => void;
}

export const useSettingsStore = create<SettingsState>((set) => {
  const initial = loadFromStorage();

  return {
    ...initial,

    set(key, value) {
      set((state) => {
        const next = { ...state, [key]: value };
        saveToStorage(next);
        return next;
      });
    },

    reset() {
      saveToStorage(DEFAULTS);
      set(DEFAULTS);
    },
  };
});
